import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { useRouter } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useRef } from "react"

import type {
  NewTalentDraft,
  TalentDraft,
} from "@/lib/utils/talent-draft.utils"

import { toast } from "@/components/ui/toast"
import { BOSS_SEASONS } from "@/core/talent-tracking/seasons.js"
import { talentConfigKeys } from "@/lib/query-keys"
import {
  EMPTY_NEW_TALENT_DRAFT,
  onNewBossAdded,
  resetBossTalentDraft,
  setBossTalentDrafts,
  setNewTalentDraft,
  setSelectedBossId,
  setSelectedSeason,
  useSelectedBossId,
  useSelectedSeason,
  type TalentConfigUiStore,
} from "@/lib/stores/talent-config-ui-store"
import {
  talentConfigApi,
  type TalentCategory,
  type TalentConfigBoss,
  type TalentConfigSeason,
  type TalentConfigSnapshot,
  type TalentConfigTalent,
} from "@/lib/talent-config-api"
import { getNextTalentSlot } from "@/lib/utils/talent-config.utils"

export type { NewTalentDraft, TalentDraft }

export const DELETE_BOSS_MUTATION_KEY = [
  "talent-config",
  "delete-boss",
] as const
export const DELETE_TALENT_MUTATION_KEY = [
  "talent-config",
  "delete-talent",
] as const

const invalidateSnapshot = (qc: QueryClient) =>
  qc.invalidateQueries({ queryKey: talentConfigKeys.snapshot() })

const useCanShowSuccessToast = () => {
  const router = useRouter()
  const isMounted = useRef(false)

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  return useCallback(() => {
    const pathname = router.state.location.pathname
    return (
      isMounted.current &&
      (pathname === "/talent-config" || pathname === "/talent-config/")
    )
  }, [router])
}

export const useConfigQuery = () =>
  useQuery({
    queryKey: talentConfigKeys.snapshot(),
    queryFn: talentConfigApi.getSnapshot,
  })

// Reset season if it no longer exists in snapshot
export const useResetSeason = (
  store: TalentConfigUiStore,
  snapshot: TalentConfigSnapshot | undefined,
) => {
  const selectedSeason = useSelectedSeason(store)

  useEffect(() => {
    if (!snapshot) {
      return
    }

    if (!snapshot.seasons.some((season) => season.season === selectedSeason)) {
      setSelectedSeason(store, snapshot.seasons[0]?.season ?? BOSS_SEASONS[0])
    }
  }, [snapshot, selectedSeason, store])
}

export const useSelectedBoss = (
  store: TalentConfigUiStore,
  snapshot: TalentConfigSnapshot | undefined,
) => {
  const selectedBossId = useSelectedBossId(store)

  return useMemo(() => {
    if (!selectedBossId || !snapshot) return null

    return snapshot.bossesById[selectedBossId] ?? null
  }, [selectedBossId, snapshot])
}

/* Update drafts when selected boss changes */
export const useUpdateDraftOnBossChange = (
  store: TalentConfigUiStore,
  selectedBoss: TalentConfigBoss | null,
) => {
  useEffect(() => {
    if (!selectedBoss) {
      resetBossTalentDraft(store)
      return
    }

    setBossTalentDrafts(store, selectedBoss)
  }, [selectedBoss, store])
}

export const useTalentConfigExportMutation = (store: TalentConfigUiStore) => {
  const canShowSuccessToast = useCanShowSuccessToast()
  return useMutation({
    mutationFn: () => talentConfigApi.exportToJson(),
    onSuccess: (result) => {
      store.setState({ pageError: null })
      if (canShowSuccessToast()) {
        toast.success(
          `Downloaded ${result.fileName} with ${result.bossCount} bosses and ${result.talentCount} talents`,
        )
      }
    },
    onError: (error) => {
      store
        .getState()
        .setErrorState(
          error instanceof Error
            ? error.message
            : "Failed to export talent config",
        )
    },
  })
}

export const useDeleteBossMutation = (store: TalentConfigUiStore) => {
  const queryClient = useQueryClient()
  const canShowSuccessToast = useCanShowSuccessToast()
  return useMutation({
    mutationKey: DELETE_BOSS_MUTATION_KEY,
    mutationFn: (bossId: string) => talentConfigApi.deleteBoss(bossId),
    onSuccess: async () => {
      store.setState({ pageError: null, selectedBossId: null })
      if (canShowSuccessToast()) {
        toast.success("Boss deleted successfully")
      }
      await invalidateSnapshot(queryClient)
    },
    onError: (error) => {
      store
        .getState()
        .setErrorState(
          error instanceof Error ? error.message : "Failed to delete boss",
        )
    },
  })
}

export const useDeleteTalentMutation = (store: TalentConfigUiStore) => {
  const queryClient = useQueryClient()
  const canShowSuccessToast = useCanShowSuccessToast()
  return useMutation({
    mutationKey: DELETE_TALENT_MUTATION_KEY,
    mutationFn: (talentId: string) => talentConfigApi.deleteTalent(talentId),
    onSuccess: async () => {
      store.setState({ pageError: null })
      if (canShowSuccessToast()) {
        toast.success("Talent deleted successfully")
      }
      await invalidateSnapshot(queryClient)
    },
    onError: (error) => {
      store
        .getState()
        .setErrorState(
          error instanceof Error ? error.message : "Failed to delete talent",
        )
    },
  })
}

type CreateTalentInput = {
  boss: TalentConfigBoss
  category: TalentCategory
  draft: NewTalentDraft
}
export const useCreateTalentMutation = (store: TalentConfigUiStore) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ boss, category, draft }: CreateTalentInput) => {
      const nextSlot = getNextTalentSlot(boss, category)

      if (!nextSlot) {
        throw new Error(`No empty ${category} slot remains for ${boss.name}`)
      }

      return talentConfigApi.createTalent({
        bossId: boss.id,
        talentId: `${boss.id}-${nextSlot.tier}`,
        category,
        tier: nextSlot.tier,
        description: draft.description,
        reward: draft.reward,
      })
    },
    onSuccess: async (_talent, variables) => {
      store.setState({ pageError: null })
      setNewTalentDraft(store, variables.category, EMPTY_NEW_TALENT_DRAFT)
      await invalidateSnapshot(queryClient)
    },
    onError: (error) => {
      store
        .getState()
        .setErrorState(
          error instanceof Error ? error.message : "Failed to create talent",
        )
    },
  })
}

export type UpdateTalentInput = {
  talent: TalentConfigTalent
  draft: TalentDraft
}

export const useUpdateTalentMutation = (store: TalentConfigUiStore) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ draft, talent }: UpdateTalentInput) =>
      talentConfigApi.updateTalent(talent.id, {
        bossId: talent.bossId,
        category: talent.category,
        tier: talent.tier,
        description: draft.description,
        reward: draft.reward,
      }),
    onSuccess: async () => {
      store.setState({ pageError: null })
      await invalidateSnapshot(queryClient)
    },
    onError: (error) => {
      store
        .getState()
        .setErrorState(
          error instanceof Error ? error.message : "Failed to update talent",
        )
    },
  })
}

export type NewBossDraft = {
  season: TalentConfigSeason
  name: string
  sortOrder: number
}

export const useCreateBossMutation = (store: TalentConfigUiStore) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (draft: NewBossDraft) => talentConfigApi.createBoss(draft),
    onSuccess: async (boss) => {
      store.setState({ pageError: null })
      await invalidateSnapshot(queryClient)
      onNewBossAdded(store, boss)
    },
    onError: (error) => {
      store
        .getState()
        .setErrorState(
          error instanceof Error ? error.message : "Failed to create boss",
        )
    },
  })
}

export type BossDraft = {
  season: TalentConfigSeason
  name: string
  sortOrder: number
}

type UpdateBossInput = {
  bossId: string
  draft: BossDraft
}

export const useUpdateBossMutation = (store: TalentConfigUiStore) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ bossId, draft }: UpdateBossInput) =>
      talentConfigApi.updateBoss(bossId, draft),
    onSuccess: async () => {
      store.setState({ pageError: null })
      await invalidateSnapshot(queryClient)
    },
    onError: (error) => {
      store
        .getState()
        .setErrorState(
          error instanceof Error ? error.message : "Failed to update boss",
        )
    },
  })
}
